import { createBetSlipItem } from '@mobi/betslip/helpers/createBetSlipItem'
import { isFobSportsSelection } from '@mobi/betslip/helpers/typeGuards'
import { addItemsToBetSlip } from '@mobi/betslip/Store/Bets'
import { store } from '@core/Store'
import { QUICKBET_MODAL_ID } from '@core/Areas/Quickbet/constants'
import { Quickbet } from '@core/Areas/Quickbet/Quickbet'
import {
  QuickbetClosed,
  QuickbetLoadMultiSelection,
  QuickbetLoadSelection,
  QuickbetSelection,
} from '@core/Areas/Quickbet/signals'
import { openModal } from '@core/Components/Modal/store'
import { triggerHapticFeedback } from '@core/Utils/hapticFeedback/hapticFeedback'
import { setBetSlipIsOpen, toggleIsAppHeaderVisible } from '@core/Areas/AppHeader/Store'
import { selectBetSlipMultiLegs } from '@mobi/betslip/Store/Bets/selectors'
import { showAddedToBetSlipToast, showMultiBuilderToast } from '@core/Areas/BetSlip/helpers/toasts'
import { getEnhancedBetslipSetting } from '@core/Areas/Settings/Store/selectors'
import { trackAddToBetSlip } from '@core/Areas/BetSlip/analytics'
import {
  hasEnoughItemsToCreateValidMulti,
  isSelectionAllowedInMulti,
} from '@mobi/betslip/helpers/state'
import { convertToBetSelection } from './helpers/convertToBetSelection'
import { animateSelectionButtonToBetslip } from './helpers/animateSelectionButtonToBetslip'

export async function handleBetSelection({
  betFlow,
  selections,
  investmentWin,
  investmentPlace,
  isEachWay,
}: {
  betFlow: BetFlow
  selections: QuickbetSelection[]
  investmentWin?: number
  investmentPlace?: number
  isEachWay?: boolean
}) {
  // ========
  // Bet Slip
  // ========
  if (betFlow.location === 'BetSlip') {
    const isDirectToBetSlip = betFlow.options?.isDirectToBetSlip ?? true
    const clickedElement = betFlow.options?.clickedElement

    const items = selections.map(selection =>
      createBetSlipItem(convertToBetSelection(selection), {
        investmentWin,
        investmentPlace,
        isEachWay,
      })
    )

    trackAddToBetSlip(items)

    store.dispatch(addItemsToBetSlip(items))
    // show header when adding item so we can see betslip number go up
    store.dispatch(toggleIsAppHeaderVisible(true))

    triggerHapticFeedback('impact-light')

    const multiLegs = selectBetSlipMultiLegs(store.getState())

    if (clickedElement && (multiLegs.length > 0 || isDirectToBetSlip)) {
      setTimeout(() => {
        animateSelectionButtonToBetslip(clickedElement)
      })
    }

    if (
      items.length === 1 &&
      multiLegs.length > 0 &&
      hasEnoughItemsToCreateValidMulti(multiLegs) &&
      (isFobSportsSelection(items[0].selection) ||
        (getEnhancedBetslipSetting(store.getState()) &&
          isSelectionAllowedInMulti(items[0].selection)))
    ) {
      showMultiBuilderToast(items[0])
      return
    }

    if (!isDirectToBetSlip && betFlow.location === 'BetSlip') {
      store.dispatch(setBetSlipIsOpen(true))
    }
    showAddedToBetSlipToast()
    return
  }

  // ========
  // Quickbet
  // ========
  // Note: isFobSportsSelection only used to allow new sports w/ kambi data
  if (betFlow.location === 'Quickbet') {
    selections.length > 1
      ? QuickbetLoadMultiSelection({
          betSource: selections[0].betSource,
          betSourceShareOrigin: selections[0].betSourceShareOrigin,
          selectionMultiLegs: selections.map(selection =>
            createBetSlipItem(convertToBetSelection(selection))
          ),
        })
      : QuickbetLoadSelection(selections[0])

    if (betFlow.options?.setUp) betFlow.options.setUp()

    store.dispatch(
      openModal({
        id: QUICKBET_MODAL_ID,
        modalComponent: Quickbet,
        color: 'dark',
        onCloseCallback: QuickbetClosed,
      })
    )
  }
}

// =====
// Types
// =====

type BetFlow =
  | {
      location: 'BetSlip'
      options?: {
        /** @default true */
        isDirectToBetSlip?: boolean
        clickedElement?: HTMLButtonElement
      }
    }
  | { location: 'Quickbet'; options?: { setUp?(): void } }
